'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import type { AppModule } from '@/lib/modules';
import { isSidebarItemAllowed } from '@/lib/modules';

interface TenantContextType {
    tenantId: string | null;
    clinicName: string | null;
    subdomain: string | null;
    logoUrl: string | null;
    settings: Record<string, unknown>;
    isLoading: boolean;
    refetch: () => Promise<void>;
    // Module system
    module: AppModule;
    /** Check if a sidebar item should be visible for current module */
    showSidebarItem: (href: string, section: 'clinical' | 'features') => boolean;
}

const TenantContext = createContext<TenantContextType>({
    tenantId: null,
    clinicName: null,
    subdomain: null,
    logoUrl: null,
    settings: {},
    isLoading: true,
    refetch: async () => {},
    module: 'full',
    showSidebarItem: () => true,
});

export function TenantProvider({ children }: { children: React.ReactNode }) {
    const [data, setData] = useState<{
        tenantId: string | null;
        clinicName: string | null;
        subdomain: string | null;
        logoUrl: string | null;
        settings: Record<string, unknown>;
    }>({
        tenantId: null,
        clinicName: null,
        subdomain: null,
        logoUrl: null,
        settings: {},
    });
    const [isLoading, setIsLoading] = useState(true);
    // A patient on the portal is not the clinic's staff: /api/tenant/current answers them 401, and a tab title set from
    // "no clinic" would put the platform's name over the clinic's own. The portal names its own tab, so nothing loads here.
    const onPortal = usePathname().startsWith('/portal');

    const fetchTenant = useCallback(async () => {
        if (onPortal) return;
        try {
            const res = await fetch('/api/tenant/current');
            const json = await res.json();
            if (json.success && json.tenant) {
                setData({
                    tenantId: json.tenant.id,
                    clinicName: json.tenant.clinicName,
                    subdomain: json.tenant.subdomain,
                    logoUrl: json.tenant.logoUrl,
                    settings: json.settings || {},
                });
            }
        } catch (err) {
            console.error('[TenantProvider] Failed to fetch tenant:', err);
        } finally {
            setIsLoading(false);
        }
    }, [onPortal]);

    useEffect(() => {
        fetchTenant();
    }, [fetchTenant]);

    // Refetch on window focus (like SWR revalidateOnFocus)
    useEffect(() => {
        const onFocus = () => fetchTenant();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [fetchTenant]);

    // Dynamic browser tab title: "ClirxOS - Phòng khám BS Hy"
    useEffect(() => {
        if (isLoading) return;
        const platformName = (data.settings?.PLATFORM_NAME as string) || 'ClirxOS';
        document.title = data.clinicName
            ? `${platformName} - ${data.clinicName}`
            : platformName;
    }, [data.clinicName, data.settings, isLoading]);

    // Derive module from settings
    const module = useMemo<AppModule>(() => {
        const m = data.settings?.module;
        if (m === 'pharmacy' || m === 'clinic' || m === 'full') return m;
        return 'full'; // backward compatible default
    }, [data.settings]);

    const showSidebarItem = useCallback((href: string, section: 'clinical' | 'features') => {
        return isSidebarItemAllowed(module, href, section);
    }, [module]);

    return (
        <TenantContext.Provider value={{ ...data, isLoading, refetch: fetchTenant, module, showSidebarItem }}>
            {children}
        </TenantContext.Provider>
    );
}

export function useTenant(): TenantContextType {
    return useContext(TenantContext);
}
